import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isNativeApp } from './apiConfig';

/**
 * Initializes native mobile features on startup:
 * - Configures Status Bar for Android / iOS
 * - Hides Splash Screen smoothly
 * - Hooks Android hardware back button to React Router
 */
export async function initNativeMobileApp(onHardwareBack?: () => boolean): Promise<void> {
  // Attach mobile soft-keyboard focus-into-view and outside-tap blur behavior across Web, Android, and iOS
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const isEditableElement = (el: Element | null): el is HTMLElement => {
      if (!el || !(el instanceof HTMLElement)) return false;
      const tag = el.tagName.toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    };

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as Element | null;
      if (!isEditableElement(target)) return;
      if (window.innerWidth <= 1024) {
        window.setTimeout(() => {
          try {
            target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          } catch {
            // ignore
          }
        }, 220);
      }
    };

    const handleVisualViewportResize = () => {
      const active = document.activeElement;
      if (isEditableElement(active) && window.innerWidth <= 1024) {
        try {
          active.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        } catch {
          // ignore
        }
      }
    };

    document.addEventListener('focusin', handleFocusIn, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize, { passive: true });
    }
  }

  if (!isNativeApp()) {
    return;
  }

  try {
    // 1. Status Bar Setup
    await StatusBar.setStyle({ style: Style.Dark });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#030712' });
      await StatusBar.setOverlaysWebView({ overlay: false });
    }
  } catch (err) {
    console.warn('Native status bar initialization notice:', err);
  }

  try {
    // 2. Hide Splash Screen after brief branded delay
    setTimeout(async () => {
      try {
        await SplashScreen.hide({ fadeOutDuration: 300 });
      } catch (e) {
        // ignore
      }
    }, 400);
  } catch (err) {
    console.warn('Native splash screen notice:', err);
  }

  try {
    // 3. Android Hardware Back Button
    App.addListener('backButton', ({ canGoBack }) => {
      // If a custom modal handler or navigation callback consumed it, do not exit
      if (onHardwareBack && onHardwareBack()) {
        return;
      }

      if (canGoBack) {
        window.history.back();
      } else {
        // Double-check before minimizing
        App.minimizeApp();
      }
    });
  } catch (err) {
    console.warn('Native back button listener notice:', err);
  }
}

/**
 * Captures a photo using the native device camera or gallery.
 * Falls back gracefully on web or if native permissions are denied.
 */
export async function capturePhoto(options?: {
  promptLabelHeader?: string;
  allowEditing?: boolean;
  source?: 'camera' | 'photos' | 'prompt';
}): Promise<{ base64?: string; dataUrl?: string; format?: string } | null> {
  if (!isNativeApp()) {
    return null; // Signals caller to use standard HTML5 <input type="file">
  }

  try {
    let chosenSource = CameraSource.Prompt;
    if (options?.source === 'camera') chosenSource = CameraSource.Camera;
    if (options?.source === 'photos') chosenSource = CameraSource.Photos;

    const image = await Camera.getPhoto({
      quality: 85,
      allowEditing: options?.allowEditing ?? false,
      resultType: CameraResultType.DataUrl,
      source: chosenSource,
      promptLabelHeader: options?.promptLabelHeader || 'Select Photo',
      promptLabelPhoto: 'From Gallery',
      promptLabelPicture: 'Take Live Photo',
    });

    return {
      dataUrl: image.dataUrl,
      format: image.format,
      base64: image.base64String,
    };
  } catch (err: any) {
    if (err?.message?.includes('User cancelled')) {
      return null;
    }
    console.warn('Native camera capture failed, falling back:', err);
    return null;
  }
}
